import React, { useRef, useState } from "react";
import { View, Text, Image, TouchableOpacity, Modal, StyleSheet } from "react-native";
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from "firebase/storage";
import { storageFunction } from "@stores/environments/Database.web";
import { dataURLtoBlob, resizeFile } from "@utils/imageUtils";
import SpaceW from "@comps/SpaceW";
import SpaceH from "@comps/SpaceH";
import { IMedia } from "@interfaces/imedia";
import { StarIcon } from "@phosphor-icons/react/dist/ssr";
import { TrashIcon } from "@phosphor-icons/react";
import { uuidv4 } from "@utils/idUtils";

interface PhotoUploaderProps {
  postId: string;
  media: IMedia[];
  setMedia: (media: IMedia[]) => void;
  mainMediaUrl: string;
  setMainMediaUrl: (url: string) => void;
  showAudio?: boolean; // NOVO
  showVideo?: boolean; // NOVO
}

const PhotoUploader: React.FC<PhotoUploaderProps> = ({
  postId,
  media,
  setMedia,
  mainMediaUrl,
  setMainMediaUrl,
  showAudio = true, // valor padrão true
  showVideo = true, // valor padrão true
}) => {
  const [uploadProgress, setUploadProgress] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [audioToDelete, setAudioToDelete] = useState<string | null>(null);

  const imageInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const audioInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
    type: 'image' | 'video' | 'audio'
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setIsUploading(true);
      let blob: any;

      if (type === 'image') {
        const image = await resizeFile(file);
        blob = dataURLtoBlob(image as string);
      } else {
        blob = file;
      }

      const ext = type === 'image' ? 'jpg' : type === 'video' ? 'mp4' : 'mp3';
      const path = `posts/${postId}/${type}/${uuidv4()}.${ext}`;
      const storageRef = ref(storageFunction, path);
      const uploadTask = uploadBytesResumable(storageRef, blob);

      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(`${progress.toFixed(0)}%`);
        },
        (error) => {
          setIsUploading(false);
        },
        () => {
          getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
            const newMedia: IMedia = { type, url: downloadURL };
            setMedia([...media, newMedia]);
            setUploadProgress('');
            setIsUploading(false);
          });
        }
      );
    } catch {
      setIsUploading(false);
    }
  };

  const deleteMedia = async (url: string) => {
    const mediaRef = ref(storageFunction, url);
    try {
      await deleteObject(mediaRef);
    } catch {}

    const updated = media.filter((m) => m.url !== url);
    setMedia(updated);

    if (mainMediaUrl === url) {
      setMainMediaUrl('');
    }
  };

  const confirmDeleteAudio = (url: string) => {
    setAudioToDelete(url);
    setShowConfirmModal(true);
  };

  const handleDeleteConfirmed = () => {
    if (audioToDelete) deleteMedia(audioToDelete);
    setShowConfirmModal(false);
    setAudioToDelete(null);
  };

  const renderMedia = (m: IMedia, index: number) => {
    const isMain = m.url === mainMediaUrl;
    const size = 100;
    const label = {
      image: '🖼️ Imagem',
      video: '🎬 Vídeo',
      audio: '🎵 Áudio',
    }[m.type];

    // NÃO renderiza áudio se showAudio = false
    if (m.type === 'audio' && !showAudio) return null;

    // NÃO renderiza vídeo se showVideo = false
    if (m.type === 'video' && !showVideo) return null;

    return (
      <View key={index} style={styles.mediaItem}>
        <Text style={styles.mediaTypeLabel}>{label}</Text>
        {m.type === 'image' ? (
          <Image source={{ uri: m.url }} style={[styles.media, { width: size, height: size }]} resizeMode="contain" />
        ) : m.type === 'video' ? (
          <video controls style={[styles.media, { width: size, height: size }]}>
            <source src={m.url} type="video/mp4" />
          </video>
        ) : (
          <audio controls style={{ width: size }}>
            <source src={m.url} type="audio/mpeg" />
          </audio>
        )}
        <SpaceH />
        {m.type !== 'audio' && (
          <TouchableOpacity onPress={() => setMainMediaUrl(m.url)} style={styles.actionRow}>
            <StarIcon size={16} color={isMain ? 'orange' : 'gray'} />
            <SpaceW />
            <Text style={{ color: isMain ? 'orange' : 'gray' }}>
              {isMain ? 'Principal' : 'Definir como principal'}
            </Text>
          </TouchableOpacity>
        )}
        <SpaceH />
        <TouchableOpacity
          onPress={() => m.type === 'audio' ? confirmDeleteAudio(m.url) : deleteMedia(m.url)}
          style={styles.actionRow}
        >
          <TrashIcon name="trash" size={16} color="red" />
          <SpaceW />
          <Text style={{ color: 'red' }}>Remover</Text>
        </TouchableOpacity>
      </View>
    );
  };

  return (
    <View>
      <Text style={styles.section}>Galeria de Mídia:</Text>
      <View style={styles.mediaContainer}>
        {media.map(renderMedia)}
      </View>

      <Text style={styles.section}>Imagem:</Text>
      <input
        type="file"
        accept="image/*"
        onChange={(e) => handleFileChange(e, 'image')}
        ref={imageInputRef}
        style={styles.inputFile}
      />

      {showVideo && (
        <>
          <Text style={styles.section}>Vídeo:</Text>
          <input
            type="file"
            accept="video/mp4"
            onChange={(e) => handleFileChange(e, 'video')}
            ref={videoInputRef}
            style={styles.inputFile}
          />
        </>
      )}

      {showAudio && (
        <>
          <Text style={styles.section}>Áudio:</Text>
          <input
            type="file"
            accept="audio/mp3,audio/mpeg"
            onChange={(e) => handleFileChange(e, 'audio')}
            ref={audioInputRef}
            style={styles.inputFile}
          />
        </>
      )}

      <Modal visible={isUploading} transparent>
        <View style={styles.modalContainer}>
          <View style={styles.modalContent}>
            <Text>Upload em progresso...</Text>
            <Text>{uploadProgress}</Text>
          </View>
        </View>
      </Modal>

      <Modal visible={showConfirmModal} transparent>
        <View style={styles.modalContainer}>
          <View style={styles.modalContent}>
            <Text>Deseja mesmo excluir este áudio?</Text>
            <View style={{ flexDirection: 'row', marginTop: 15 }}>
              <TouchableOpacity onPress={() => setShowConfirmModal(false)} style={{ marginRight: 20 }}>
                <Text style={{ color: 'gray' }}>Cancelar</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={handleDeleteConfirmed}>
                <Text style={{ color: 'red' }}>Deletar</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
};

export default PhotoUploader;

const styles = StyleSheet.create({
  section: { marginTop: 15, fontWeight: "bold" },
  mediaContainer: { flexDirection: "row", flexWrap: "wrap" },
  mediaItem: { marginRight: 15, marginBottom: 25, alignItems: 'center' },
  mediaTypeLabel: { fontWeight: 'bold', marginBottom: 5, textAlign: 'center' },
  media: { borderRadius: 10 },
  actionRow: { flexDirection: 'row', alignItems: 'center', marginTop: 5 },
  inputFile: { marginTop: 10, marginBottom: 10 },
  modalContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "rgba(0,0,0,0.5)",
  },
  modalContent: {
    backgroundColor: "white",
    padding: 20,
    borderRadius: 10,
  },
});
